import {
  COMPANION_FIELD_KEYS,
  COMPANION_FIELD_LABELS,
  getCompanionFieldLabels,
  type CompanionFieldKey,
  type CompanionFieldMode,
} from "@/lib/companionFields";
import { normalizeLicensePlateForStorage } from "@/lib/licensePlate";
import type { BookingMessages } from "@/i18n/types";

export type PrimaryGuestFieldsConfig = Record<CompanionFieldKey, CompanionFieldMode>;

export type PrimaryGuestFormData = Record<CompanionFieldKey, string>;

export const DEFAULT_PRIMARY_GUEST_FIELDS: PrimaryGuestFieldsConfig = {
  title: "required",
  first_name: "required",
  middle_name: "off",
  last_name: "required",
  company_name: "off",
  vat_id: "off",
  email: "required",
  phone: "optional",
  license_plate: "optional",
  age: "off",
  date_of_birth: "off",
  nationality: "off",
  street: "off",
  house_number: "off",
  address_line_2: "off",
  postal_code: "off",
  city: "off",
  state_region: "off",
  country_code: "off",
};

export function emptyPrimaryGuestForm(): PrimaryGuestFormData {
  const form = Object.fromEntries(COMPANION_FIELD_KEYS.map((k) => [k, ""])) as PrimaryGuestFormData;
  form.country_code = "DE";
  return form;
}

export function activePrimaryGuestFields(config: PrimaryGuestFieldsConfig): CompanionFieldKey[] {
  return COMPANION_FIELD_KEYS.filter(
    (k) => config[k] !== "off" && k !== "age",
  );
}

export function getMissingPrimaryGuestFields(
  form: PrimaryGuestFormData,
  config: PrimaryGuestFieldsConfig,
): CompanionFieldKey[] {
  const missing: CompanionFieldKey[] = [];
  for (const key of COMPANION_FIELD_KEYS) {
    if (config[key] !== "required") continue;
    if (!form[key].trim()) missing.push(key);
  }
  return missing;
}

export function validatePrimaryGuest(
  form: PrimaryGuestFormData,
  config: PrimaryGuestFieldsConfig,
  messages?: BookingMessages,
): string | null {
  const missing = getMissingPrimaryGuestFields(form, config);
  if (missing.length === 0) return null;
  const labels = messages ? getCompanionFieldLabels(messages) : COMPANION_FIELD_LABELS;
  const labelList = missing.map((k) => labels[k]).join(", ");
  return messages
    ? messages.validation.primaryGuestRequiredFields(labelList)
    : `Bitte füllen Sie folgende Pflichtfelder aus: ${labelList}.`;
}

export function primaryGuestSummaryLines(
  form: PrimaryGuestFormData,
  config: PrimaryGuestFieldsConfig,
  messages?: BookingMessages,
): { key: CompanionFieldKey; label: string; value: string }[] {
  const fieldLabels = messages ? getCompanionFieldLabels(messages) : COMPANION_FIELD_LABELS;
  const lines: { key: CompanionFieldKey; label: string; value: string }[] = [];
  for (const key of COMPANION_FIELD_KEYS) {
    if (config[key] === "off" || key === "age") continue;
    const value = form[key].trim();
    if (!value) continue;
    let display = value;
    if (key === "date_of_birth") {
      const [y, m, d] = value.split("-");
      if (y && m && d) display = `${d}.${m}.${y}`;
    }
    lines.push({ key, label: fieldLabels[key], value: display });
  }
  return lines;
}

export function buildPrimaryGuestPayload(
  form: PrimaryGuestFormData,
  config: PrimaryGuestFieldsConfig,
): Record<string, string | null> {
  const payload: Record<string, string | null> = {};
  for (const key of COMPANION_FIELD_KEYS) {
    if (config[key] === "off" || key === "age") continue;
    const value = form[key].trim();
    payload[key] =
      key === "license_plate" && value
        ? normalizeLicensePlateForStorage(value)
        : key === "country_code"
          ? value.trim().toUpperCase().slice(0, 2) || "DE"
          : value || null;
  }
  return payload;
}
